<template>
  <div class="mmp-page">
    <!-- ── Top context bar ─────────────────────────────────────────── -->
    <div class="mmp-topbar">
      <div class="mmp-selects">
        <div class="mmp-field">
          <label class="mmp-label">Mavsum (kengash)</label>
          <el-select
            v-model="boardId"
            class="mmp-select"
            filterable
            placeholder="Mavsumni tanlang"
            :loading="boardsLoading"
            @change="onContextChange"
          >
            <el-option
              v-for="board in boards"
              :key="board.id"
              :value="board.id"
              :label="boardLabel(board)"
            />
          </el-select>
        </div>

        <div class="mmp-field">
          <label class="mmp-label">Tashabbus kategoriyasi</label>
          <el-select
            v-model="categoryId"
            class="mmp-select"
            filterable
            placeholder="Kategoriyani tanlang"
            :loading="categoriesLoading"
            @change="onContextChange"
          >
            <el-option
              v-for="cat in categories"
              :key="cat.id"
              :value="cat.id"
              :label="cat.title"
            />
          </el-select>
        </div>
      </div>

      <!-- Sync control + status chip -->
      <div class="mmp-sync">
        <div class="mmp-sync-chip" :class="`is-${syncState}`">
          <el-icon v-if="syncState === 'running'" class="is-spin"><Loading /></el-icon>
          <el-icon v-else-if="syncState === 'success'"><CircleCheck /></el-icon>
          <el-icon v-else-if="syncState === 'failed'"><CircleClose /></el-icon>
          <el-icon v-else><Warning /></el-icon>
          <div class="mmp-sync-text">
            <span class="mmp-sync-title">{{ syncTitle }}</span>
            <span class="mmp-sync-sub">{{ syncSub }}</span>
            <span class="mmp-sync-last">{{ lastSyncText }}</span>
          </div>
        </div>
        <el-button
          type="primary"
          :icon="Refresh"
          :loading="syncStarting"
          :disabled="!canSyncNow"
          @click="startSync"
        >
          Sinxronlash
        </el-button>

        <!-- Excel report: enabled once a board is chosen. "Joriy turkum" narrows
             to the selected category; "Barcha turkumlarim" = full board scope. -->
        <el-dropdown trigger="click" :disabled="!boardId || reportDownloading" @command="downloadReport">
          <el-button :icon="Download" :loading="reportDownloading" :disabled="!boardId">
            Hisobot (Excel)<el-icon class="el-icon--right"><ArrowDown /></el-icon>
          </el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="current" :disabled="!categoryId">
                Joriy turkum
              </el-dropdown-item>
              <el-dropdown-item command="all">Barcha turkumlarim</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>

    <!-- Board window banner (review period started → read-only) -->
    <div v-if="contextReady && windowClosed" class="mmp-window-banner">
      <el-icon><Lock /></el-icon>
      <span>
        Bu mavsum uchun tovar qo'shish / tahrirlash / o'chirish muddati tugagan —
        review boshlangan. Ro'yxatni faqat ko'rish mumkin.
      </span>
    </div>

    <!-- ── Workspace: catalog picker + product list ───────────────── -->
    <div class="mmp-workspace">
      <!-- LEFT: catalog picker -->
      <section class="mmp-pane mmp-catalog">
        <div class="mmp-pane-head">
          <span class="mmp-pane-title">Kataloq (tasniflagich)</span>
          <el-radio-group v-model="catalogMode" size="small" @change="onModeChange">
            <el-radio-button label="tree">Daraxt</el-radio-button>
            <el-radio-button label="search">Qidiruv</el-radio-button>
          </el-radio-group>
        </div>

        <!-- Search mode -->
        <div v-if="catalogMode === 'search'" class="mmp-search-box">
          <el-input
            v-model="catalogSearch"
            clearable
            placeholder="Nomi yoki kodi bo'yicha qidiring"
            :prefix-icon="Search"
            @input="onCatalogSearch"
            @clear="onCatalogSearch"
          />
        </div>

        <!-- Tree breadcrumb -->
        <div v-else class="mmp-breadcrumb">
          <button
            v-for="(node, idx) in catalogPath"
            :key="node.id"
            class="mmp-crumb"
            :class="{ 'is-current': idx === catalogPath.length - 1 }"
            @click="goToCrumb(idx)"
          >
            <el-icon v-if="idx > 0" class="mmp-crumb-sep"><ArrowRight /></el-icon>
            <span>{{ node.title }}</span>
          </button>
        </div>

        <div v-loading="catalogLoading" class="mmp-pane-body">
          <!-- Catalog empty (never synced) -->
          <div v-if="catalogEmptyState" class="mmp-empty">
            <el-icon :size="40"><Box /></el-icon>
            <p>Kataloq bo'sh — avval sinxronlang</p>
            <el-button type="primary" :disabled="!canSyncNow" @click="startSync">
              Sinxronlash
            </el-button>
          </div>

          <template v-else>
            <!-- Tree: subcategories -->
            <ul
              v-if="catalogMode === 'tree' && catalogCategories.length"
              class="mmp-cat-list"
            >
              <li
                v-for="cat in catalogCategories"
                :key="cat.id"
                class="mmp-cat-item"
                @click="openCategory(cat)"
              >
                <div class="mmp-cat-main">
                  <span class="mmp-cat-title">{{ cat.title }}</span>
                  <span class="mmp-cat-code">{{ cat.code }}</span>
                </div>
                <div class="mmp-cat-meta">
                  <span v-if="cat.products_count" class="mmp-cat-count">
                    {{ cat.products_count }} ta
                  </span>
                  <el-icon v-if="cat.has_children"><ArrowRight /></el-icon>
                </div>
              </li>
            </ul>

            <!-- Product rows (tree leaf or search results) -->
            <ul v-if="catalogProducts.length" class="mmp-prod-list">
              <li
                v-for="prod in catalogProducts"
                :key="prod.id"
                class="mmp-prod-item"
                :class="{ 'is-disabled': !prod.selectable }"
              >
                <div class="mmp-prod-main">
                  <span class="mmp-prod-title" :title="prod.title">{{ prod.title }}</span>
                  <span class="mmp-prod-code">{{ prod.code }}</span>
                </div>
                <span class="mmp-unit-chip" :class="{ 'is-muted': !prod.selectable }">
                  {{ unitLabel(prod.unit) }}
                </span>
                <el-button
                  v-if="prod.selectable && !isAdded(prod.id)"
                  type="primary"
                  size="small"
                  :icon="Plus"
                  :loading="addingId === prod.id"
                  :disabled="windowClosed"
                  @click="addProduct(prod)"
                >
                  Qo'shish
                </el-button>
                <el-tag
                  v-else-if="isAdded(prod.id)"
                  type="success"
                  effect="light"
                  size="small"
                >
                  Qo'shilgan
                </el-tag>
                <el-tooltip
                  v-else
                  content="O'lchov birligi qo'llab-quvvatlanmaydi"
                  placement="top"
                >
                  <el-tag type="info" effect="plain" size="small">Mavjud emas</el-tag>
                </el-tooltip>
              </li>
            </ul>

            <!-- Nothing to show -->
            <div
              v-if="!catalogCategories.length && !catalogProducts.length && !catalogLoading"
              class="mmp-empty mmp-empty--soft"
            >
              <el-icon :size="34"><Search /></el-icon>
              <p>{{ catalogMode === 'search' ? 'Hech narsa topilmadi' : 'Bu bo\'limda mahsulot yo\'q' }}</p>
            </div>
          </template>
        </div>
      </section>

      <!-- RIGHT: product list -->
      <section class="mmp-pane mmp-list">
        <div class="mmp-pane-head">
          <span class="mmp-pane-title">Mahsulotlar ro'yxati</span>
          <el-input
            v-model="listSearch"
            class="mmp-list-search"
            clearable
            size="small"
            placeholder="Nomi bo'yicha qidirish"
            :prefix-icon="Search"
            @input="onListSearch"
            @clear="onListSearch"
          />
        </div>

        <div v-if="!contextReady" class="mmp-empty mmp-empty--soft">
          <el-icon :size="34"><Pointer /></el-icon>
          <p>Mavsum va kategoriyani tanlang</p>
        </div>

        <template v-else>
          <el-table
            v-loading="listLoading"
            :data="products"
            border
            height="calc(100vh - 320px)"
            :row-class-name="rowClass"
          >
            <el-table-column type="index" :index="indexMethod" width="56" />
            <el-table-column label="Nomi" min-width="240">
              <template #default="scope">
                <div class="mmp-cell-title">
                  <span>{{ scope.row.title }}</span>
                  <el-tag
                    v-if="isIncomplete(scope.row)"
                    size="small"
                    type="warning"
                    effect="plain"
                    class="mmp-hint-tag"
                  >
                    Narx kiritilmagan
                  </el-tag>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="O'lchov" width="100" align="center">
              <template #default="scope">
                <span class="mmp-unit-chip is-muted">
                  {{ unitLabel(scope.row.unit_of_measure) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column label="Narxi" width="170" align="right">
              <template #default="scope">
                <el-input-number
                  v-model="scope.row.price"
                  :min="0"
                  :controls="false"
                  :disabled="windowClosed"
                  class="mmp-num"
                  @change="markDirty(scope.row)"
                />
              </template>
            </el-table-column>
            <el-table-column label="Soni" width="130" align="right">
              <template #default="scope">
                <el-input-number
                  v-model="scope.row.max_available"
                  :min="0"
                  :controls="false"
                  :disabled="windowClosed"
                  class="mmp-num"
                  @change="markDirty(scope.row)"
                />
              </template>
            </el-table-column>
            <el-table-column label="Holati" width="120" align="center">
              <template #default="scope">
                <el-switch
                  v-model="scope.row.is_active"
                  active-value="1"
                  inactive-value="0"
                  active-text="Faol"
                  inactive-text=""
                  inline-prompt
                  :disabled="windowClosed"
                  @change="markDirty(scope.row)"
                />
              </template>
            </el-table-column>
            <el-table-column label="Qo'shgan" min-width="180">
              <template #default="scope">
                <div class="mmp-audit">
                  <span class="mmp-audit-name">{{ scope.row.created_by_name || '—' }}</span>
                  <el-tooltip
                    v-if="scope.row.created_at"
                    :content="$moment(scope.row.created_at).format('DD-MM-YYYY / HH:mm')"
                    placement="top"
                  >
                    <span class="mmp-audit-date">
                      {{ $moment(scope.row.created_at).format('DD-MM-YYYY') }}
                    </span>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Amallar" width="150" align="center" fixed="right">
              <template #default="scope">
                <div class="mmp-row-actions">
                  <el-button
                    size="small"
                    type="primary"
                    :icon="Check"
                    :disabled="windowClosed || !isDirty(scope.row)"
                    :loading="savingId === scope.row.id"
                    @click="saveProduct(scope.row)"
                  />
                  <!-- Window closed: plain disabled button, NO popconfirm wrapper
                       (el-popconfirm can still trigger over a disabled reference). -->
                  <el-button
                    v-if="windowClosed"
                    size="small"
                    type="danger"
                    :icon="Delete"
                    disabled
                  />
                  <el-popconfirm
                    v-else
                    title="O'chirishni tasdiqlaysizmi?"
                    confirm-button-text="Ha"
                    cancel-button-text="Bekor"
                    confirm-button-type="danger"
                    :icon="WarningFilled"
                    icon-color="#f56c6c"
                    :width="220"
                    @confirm="deleteProduct(scope.row)"
                  >
                    <template #reference>
                      <el-button size="small" type="danger" :icon="Delete" />
                    </template>
                  </el-popconfirm>
                </div>
              </template>
            </el-table-column>

            <template #empty>
              <div class="mmp-empty mmp-empty--soft">
                <el-icon :size="34"><Box /></el-icon>
                <p>Bu mavsum va kategoriya uchun hali mahsulot yo'q — kataloqdan qo'shing</p>
              </div>
            </template>
          </el-table>

          <div class="mmp-pagination">
            <span>Jami: {{ totalCount }}</span>
            <el-pagination
              background
              layout="prev, pager, next"
              :total="totalCount"
              :page-size="limit"
              v-model:current-page="currentPage"
              @current-change="getProducts"
            />
          </div>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import { ElNotification, ElMessage } from "element-plus";
import {
  Loading,
  CircleCheck,
  CircleClose,
  Warning,
  Refresh,
  Search,
  ArrowRight,
  ArrowDown,
  Plus,
  Box,
  Pointer,
  Check,
  Delete,
  Download,
  WarningFilled,
  Lock,
} from "@element-plus/icons-vue";
import { blobErrorMessage } from "@/utils/common";

const UNIT_LABELS = {
  piece: "dona",
  kg: "kg",
  m: "m",
  m2: "m²",
  package: "quti",
  collection: "to'plam",
};

export default {
  name: "MinistryManagerProducts",
  components: { Loading, CircleCheck, CircleClose, Warning, ArrowRight, ArrowDown, Box, Pointer, Search, Lock },
  data() {
    return {
      // icons referenced in template attributes
      Refresh,
      Search,
      Plus,
      Check,
      Delete,
      Download,
      WarningFilled,
      reportDownloading: false,
      // language header (catalog/product titles localize by hl)
      hl: "uz_lat",

      // context
      boards: [],
      boardsLoading: false,
      boardId: null,
      categories: [],
      categoriesLoading: false,
      categoryId: null,

      // sync
      sync: null, // SyncStatus
      syncStarting: false,
      syncTimer: null,
      countdownTimer: null,
      now: Date.now(),

      // catalog
      catalogMode: "tree",
      catalogLoading: false,
      catalogPath: [{ id: 0, title: "C toifa" }],
      catalogCategories: [],
      catalogProducts: [],
      catalogSearch: "",
      catalogSearchTimer: null,
      addingId: null,

      // product list
      products: [],
      originals: {}, // id -> { price, max_available, is_active }
      totalCount: 0,
      limit: 20,
      currentPage: 1,
      listLoading: false,
      listSearch: "",
      listSearchTimer: null,
      savingId: null,
    };
  },
  computed: {
    contextReady() {
      return !!this.boardId && !!this.categoryId;
    },
    selectedBoard() {
      return this.boards.find((b) => b.id === this.boardId) || null;
    },
    windowClosed() {
      // Writes (add/edit/delete) are allowed only BEFORE the board's REVIEW
      // period starts — the whole accept period is inside the write window now
      // (FRONTEND_PRODUCT_WRITE_WINDOW.md §1/§5). Once now >= review_start_date
      // the list is read-only. Compare instants (the date carries an offset).
      const start = this.selectedBoard?.review_start_date;
      if (!start) return false;
      return this.now >= new Date(start).getTime();
    },
    windowClosedMessage() {
      return "Bu mavsum uchun qo'shish / tahrirlash / o'chirish muddati tugagan — review boshlangan";
    },
    syncState() {
      return this.sync?.status || "never";
    },
    canSyncNow() {
      if (this.syncStarting) return false;
      if (!this.sync) return true;
      return this.sync.can_sync_now;
    },
    catalogEmptyState() {
      // never synced AND nothing fetched yet
      return (
        this.syncState === "never" &&
        !this.catalogCategories.length &&
        !this.catalogProducts.length &&
        !this.catalogLoading
      );
    },
    addedIds() {
      return new Set(
        this.products.map((p) => p.tasniflagich_product_id).filter(Boolean)
      );
    },
    lastSyncText() {
      // Always visible (§4.2): the last SUCCESSFUL sync time, whatever the state.
      const at = this.sync?.last_success_at;
      if (!at) return "Hali sinxronlanmagan";
      return `Oxirgi sinxronlash: ${this.$moment(at).format("DD-MM-YYYY / HH:mm")}`;
    },
    syncTitle() {
      // "synced today" is the throttled state — surface it explicitly.
      if (this.sync?.synced_today && this.syncState !== "running") {
        return "Bugun sinxronlangan";
      }
      const map = {
        running: "Sinxronlanmoqda…",
        success: "Yangilandi",
        failed: "Xatolik",
        never: "Hali sinxronlanmagan",
      };
      return map[this.syncState] || "—";
    },
    syncSub() {
      if (!this.sync) return this.lastSyncText;
      if (this.syncState === "running") {
        return `${this.sync.categories_count || 0} bo'lim · ${this.sync.products_count || 0} mahsulot`;
      }
      if (this.syncState === "failed") {
        return (this.sync.error || "Qayta urinib ko'ring").slice(0, 60);
      }
      // success / never → always show the last successful sync time
      return this.lastSyncText;
    },
  },
  methods: {
    cfg(params) {
      return { headers: { hl: this.hl }, ...(params ? { params } : {}) };
    },
    boardLabel(b) {
      const season = b.season ? `${b.season}-mavsum` : "";
      const year = b.year ? `${b.year} yil` : "";
      return [b.title, season, year].filter(Boolean).join(" ");
    },
    unitLabel(unit) {
      return UNIT_LABELS[unit] || "—";
    },
    isWindowClosedError(response) {
      return (
        response?.status === 451 &&
        response?.data?.specific_slug === "board_window_closed"
      );
    },
    indexMethod(index) {
      return this.limit * (this.currentPage - 1) + index + 1;
    },
    rowClass({ row }) {
      // Subtle nudge for rows that are active but still have no price/count.
      return this.isIncomplete(row) ? "mmp-incomplete-row" : "";
    },
    isIncomplete(row) {
      // Active by default, but price/count not yet filled — a hint, not a status.
      return row.is_active === "1" && (!(row.price > 0) || !(row.max_available > 0));
    },

    // ── context loaders ──────────────────────────────────────────
    getBoards() {
      this.boardsLoading = true;
      this.$http
        .get("api/v1/boards", this.cfg({ status: 1 }))
        .then(({ data }) => {
          this.boards = data.boards || [];
          if (this.boards.length && !this.boardId) {
            this.boardId = this.boards[0].id;
            this.onContextChange();
          }
        })
        .catch(() => {})
        .finally(() => {
          this.boardsLoading = false;
        });
    },
    getCategories() {
      this.categoriesLoading = true;
      // Source of truth for the category select: the assigned-categories endpoint.
      // ministry_manager -> ONLY their assigned categories; admin -> all active.
      // Do NOT use /initiative-categories here (spec §3.0).
      this.$http
        .get("api/v1/ministry-manager/products-categories", this.cfg())
        .then(({ data }) => {
          this.categories = data.categories || [];
        })
        .catch(() => {})
        .finally(() => {
          this.categoriesLoading = false;
        });
    },
    onContextChange() {
      if (this.contextReady) {
        this.currentPage = 1;
        this.getProducts();
      }
    },

    // ── sync ─────────────────────────────────────────────────────
    getSyncStatus() {
      return this.$http
        .get("api/v1/ministry-manager/products-sync/status", this.cfg())
        .then(({ data }) => {
          this.sync = data;
          if (data.status === "running") {
            this.scheduleSyncPoll();
          } else {
            this.clearSyncPoll();
            if (data.status === "success" && !this.catalogCategories.length) {
              this.loadRoot();
            }
          }
        })
        .catch(() => {});
    },
    scheduleSyncPoll() {
      this.clearSyncPoll();
      this.syncTimer = setTimeout(() => this.getSyncStatus(), 4000);
    },
    clearSyncPoll() {
      if (this.syncTimer) {
        clearTimeout(this.syncTimer);
        this.syncTimer = null;
      }
    },
    startSync() {
      this.syncStarting = true;
      this.$http
        .post("api/v1/ministry-manager/products-sync", {}, this.cfg())
        .then(() => {
          ElMessage.success("Sinxronlash boshlandi");
          this.getSyncStatus();
        })
        .catch(({ response }) => {
          const slug = response?.data?.specific_slug;
          let msg = response?.data?.message || "Sinxronlashda xatolik";
          if (response?.status === 451) {
            msg =
              slug === "sync_running"
                ? "Sinxronlash davom etmoqda"
                : "Bugun allaqachon sinxronlangan, ertaga urinib ko'ring";
            this.getSyncStatus();
          }
          ElNotification({ type: "warning", title: "Sinxronlash", message: msg });
        })
        .finally(() => {
          this.syncStarting = false;
        });
    },

    // ── catalog (tree + search) ──────────────────────────────────
    onModeChange() {
      this.catalogProducts = [];
      if (this.catalogMode === "tree") {
        this.loadRoot();
      } else {
        this.catalogCategories = [];
        this.catalogSearch = "";
      }
    },
    loadRoot() {
      this.catalogPath = [{ id: 0, title: "C toifa" }];
      this.catalogProducts = [];
      this.loadCategories(0);
    },
    loadCategories(parentId) {
      this.catalogLoading = true;
      this.$http
        .get(
          "api/v1/ministry-manager/products-catalog/categories",
          this.cfg({ parent_id: parentId })
        )
        .then(({ data }) => {
          this.catalogCategories = data.categories || [];
        })
        .catch(() => {
          this.catalogCategories = [];
        })
        .finally(() => {
          this.catalogLoading = false;
        });
    },
    openCategory(cat) {
      this.catalogPath.push({ id: cat.id, title: cat.title });
      this.catalogProducts = [];
      if (cat.has_children) {
        this.loadCategories(cat.id);
      } else {
        this.catalogCategories = [];
      }
      if (cat.products_count > 0 || cat.is_leaf) {
        this.loadCategoryProducts(cat.id);
      }
    },
    loadCategoryProducts(categoryId) {
      this.catalogLoading = true;
      this.$http
        .get(
          "api/v1/ministry-manager/products-catalog/products",
          this.cfg({ category_id: categoryId, limit: 100, offset: 0 })
        )
        .then(({ data }) => {
          this.catalogProducts = data.items || [];
        })
        .catch(() => {
          this.catalogProducts = [];
        })
        .finally(() => {
          this.catalogLoading = false;
        });
    },
    goToCrumb(idx) {
      if (idx === this.catalogPath.length - 1) return;
      const node = this.catalogPath[idx];
      this.catalogPath = this.catalogPath.slice(0, idx + 1);
      this.catalogProducts = [];
      this.loadCategories(node.id);
    },
    onCatalogSearch() {
      clearTimeout(this.catalogSearchTimer);
      this.catalogSearchTimer = setTimeout(() => {
        const q = this.catalogSearch.trim();
        if (!q) {
          this.catalogProducts = [];
          return;
        }
        this.catalogLoading = true;
        this.$http
          .get(
            "api/v1/ministry-manager/products-catalog/search",
            this.cfg({ search: q, limit: 50, offset: 0 })
          )
          .then(({ data }) => {
            this.catalogProducts = data.products || [];
          })
          .catch(() => {
            this.catalogProducts = [];
          })
          .finally(() => {
            this.catalogLoading = false;
          });
      }, 400);
    },
    isAdded(tasniflagichProductId) {
      return this.addedIds.has(tasniflagichProductId);
    },
    addProduct(prod) {
      if (!this.contextReady) {
        ElMessage.warning("Avval mavsum va kategoriyani tanlang");
        return;
      }
      this.addingId = prod.id;
      this.$http
        .post(
          "api/v1/ministry-manager/products",
          {
            board_id: this.boardId,
            initiative_category_id: this.categoryId,
            tasniflagich_product_id: prod.id,
          },
          this.cfg()
        )
        .then(() => {
          ElMessage.success("Qo'shildi");
          this.currentPage = 1;
          this.getProducts();
        })
        .catch(({ response }) => {
          const status = response?.status;
          let msg = response?.data?.message || "Qo'shishda xatolik";
          if (status === 409) msg = "Bu mahsulot allaqachon qo'shilgan";
          else if (status === 403) msg = "Bu kategoriya sizga biriktirilmagan";
          else if (status === 404) msg = "Avval kataloqni sinxronlang";
          else if (status === 400) msg = "Mahsulot o'lchov birligi qo'llab-quvvatlanmaydi";
          else if (this.isWindowClosedError(response)) msg = this.windowClosedMessage;
          ElNotification({ type: "error", title: "Xatolik", message: msg });
        })
        .finally(() => {
          this.addingId = null;
        });
    },

    // ── product list ─────────────────────────────────────────────
    getProducts() {
      if (!this.contextReady) return;
      this.listLoading = true;
      this.$http
        .get(
          "api/v1/ministry-manager/products",
          this.cfg({
            board_id: this.boardId,
            initiative_category_id: this.categoryId,
            search: this.listSearch || null,
            limit: this.limit,
            offset: this.limit * (this.currentPage - 1),
          })
        )
        .then(({ data }) => {
          this.products = data.items || [];
          this.totalCount = data.count || 0;
          this.snapshotOriginals();
        })
        .catch(({ response }) => {
          ElNotification({
            type: "error",
            title: "Xatolik",
            message: response?.data?.message || "Yuklashda xatolik",
          });
        })
        .finally(() => {
          this.listLoading = false;
        });
    },
    onListSearch() {
      clearTimeout(this.listSearchTimer);
      this.listSearchTimer = setTimeout(() => {
        this.currentPage = 1;
        this.getProducts();
      }, 400);
    },
    snapshotOriginals() {
      const map = {};
      this.products.forEach((p) => {
        map[p.id] = {
          price: p.price,
          max_available: p.max_available,
          is_active: p.is_active,
        };
      });
      this.originals = map;
    },
    markDirty() {
      // no-op: dirtiness is derived in isDirty(); kept for explicit change hook
    },
    isDirty(row) {
      const o = this.originals[row.id];
      if (!o) return false;
      return (
        row.price !== o.price ||
        row.max_available !== o.max_available ||
        row.is_active !== o.is_active
      );
    },
    saveProduct(row) {
      // Publishing guard: don't publish a product with no price/count.
      if (row.is_active === "1" && (!(row.price > 0) || !(row.max_available > 0))) {
        ElNotification({
          type: "warning",
          title: "Diqqat",
          message: "Faollashtirish uchun narx va soni 0 dan katta bo'lishi kerak",
        });
        return;
      }
      this.savingId = row.id;
      this.$http
        .put(
          `api/v1/ministry-manager/products/${row.id}`,
          {
            price: row.price,
            max_available: row.max_available,
            is_active: row.is_active,
          },
          this.cfg()
        )
        .then(() => {
          ElMessage.success("Saqlandi");
          this.originals[row.id] = {
            price: row.price,
            max_available: row.max_available,
            is_active: row.is_active,
          };
        })
        .catch(({ response }) => {
          const msg = this.isWindowClosedError(response)
            ? this.windowClosedMessage
            : response?.data?.message || "Saqlashda xatolik";
          ElNotification({ type: "error", title: "Xatolik", message: msg });
        })
        .finally(() => {
          this.savingId = null;
        });
    },
    deleteProduct(row) {
      this.$http
        .delete(`api/v1/ministry-manager/products/${row.id}`, this.cfg())
        .then(() => {
          ElMessage.success("O'chirildi");
          if (this.products.length === 1 && this.currentPage > 1) {
            this.currentPage -= 1;
          }
          this.getProducts();
        })
        .catch(({ response }) => {
          let msg = response?.data?.message || "O'chirishda xatolik";
          if (response?.status === 500) {
            msg = "Bu mahsulot tashabbusda ishlatilgan, o'chirib bo'lmaydi";
          } else if (this.isWindowClosedError(response)) {
            msg = this.windowClosedMessage;
          }
          ElNotification({ type: "error", title: "Xatolik", message: msg });
        });
    },

    // ── Excel report ─────────────────────────────────────────────
    downloadReport(command) {
      if (!this.boardId) {
        ElMessage.warning("Avval mavsumni tanlang");
        return;
      }
      const params = { board_id: this.boardId };
      if (command === "current") {
        if (!this.categoryId) {
          ElMessage.warning("Avval kategoriyani tanlang");
          return;
        }
        params.initiative_category_id = this.categoryId;
      }
      this.reportDownloading = true;
      this.$http
        .get("api/v1/ministry-manager/products-report", {
          params,
          responseType: "blob",
          headers: { hl: this.hl },
        })
        .then((res) => {
          this.saveBlobFile(res);
          ElMessage.success("Hisobot yuklab olindi");
        })
        .catch(async ({ response }) => {
          const msg = await blobErrorMessage(response, "Hisobotni yuklashda xatolik");
          ElNotification({ type: "error", title: "Xatolik", message: msg });
        })
        .finally(() => {
          this.reportDownloading = false;
        });
    },
    saveBlobFile(res) {
      const url = URL.createObjectURL(
        new Blob([res.data], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
        })
      );
      let fileName = `Products-report-${this.$moment().format("YYYY-MM-DD")}.xlsx`;
      const headers = res.headers || {};
      const header =
        (headers.get && headers.get("Content-Disposition")) ||
        headers["content-disposition"];
      if (header) {
        const match = header.match(/filename[^;=\n]*=["']?([^";'\n]+)["']?/i);
        if (match && match[1]) fileName = decodeURIComponent(match[1].trim());
      }
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },
  mounted() {
    this.getBoards();
    this.getCategories();
    this.getSyncStatus().then(() => {
      if (this.catalogMode === "tree" && !this.catalogCategories.length) {
        this.loadRoot();
      }
    });
    this.countdownTimer = setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  beforeUnmount() {
    this.clearSyncPoll();
    if (this.countdownTimer) clearInterval(this.countdownTimer);
  },
};
</script>

<style scoped>
.mmp-page {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* ── Top bar ──────────────────────────────────────────────────── */
.mmp-topbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  background: #ffffff;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 6px 24px rgba(17, 24, 39, 0.08), 0 1px 3px rgba(17, 24, 39, 0.06);
}

.mmp-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid #fde68a;
  background: #fffbeb;
  color: #b45309;
  font-size: 13px;
  font-weight: 500;
}

.mmp-banner .el-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.mmp-selects {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}

.mmp-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mmp-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.mmp-select {
  width: 280px;
}

.mmp-sync {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mmp-sync-chip {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  min-width: 200px;
}

.mmp-sync-chip .el-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.mmp-sync-chip.is-running { border-color: #c7d2fe; background: #eef2ff; color: #4f46e5; }
.mmp-sync-chip.is-success { border-color: #bbf7d0; background: #f0fdf4; color: #16a34a; }
.mmp-sync-chip.is-failed { border-color: #fecaca; background: #fef2f2; color: #dc2626; }
.mmp-sync-chip.is-never { color: #9ca3af; }

.mmp-sync-text {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.mmp-sync-title { font-size: 13px; font-weight: 600; }
.mmp-sync-sub { font-size: 11px; opacity: 0.85; }
.mmp-sync-last { font-size: 11px; color: #6b7280; margin-top: 2px; }

.is-spin { animation: mmp-spin 1s linear infinite; }
@keyframes mmp-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .is-spin { animation: none; } }

/* ── Workspace ────────────────────────────────────────────────── */
.mmp-workspace {
  display: grid;
  grid-template-columns: 4fr 6fr;
  gap: 14px;
  align-items: start;
}

.mmp-pane {
  background: #ffffff;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 6px 24px rgba(17, 24, 39, 0.08), 0 1px 3px rgba(17, 24, 39, 0.06);
}

.mmp-pane-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.mmp-pane-title { font-size: 15px; font-weight: 600; color: #111827; }

.mmp-search-box { margin-bottom: 12px; }
.mmp-list-search { width: 220px; }

.mmp-breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  margin-bottom: 10px;
}

.mmp-crumb {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 13px;
  color: var(--el-color-primary);
  padding: 2px 4px;
}

.mmp-crumb.is-current { color: #374151; font-weight: 600; cursor: default; }
.mmp-crumb-sep { font-size: 12px; color: #c0c4cc; }

.mmp-pane-body { min-height: 360px; max-height: calc(100vh - 320px); overflow-y: auto; }

/* category list */
.mmp-cat-list, .mmp-prod-list { list-style: none; margin: 0; padding: 0; }

.mmp-cat-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 12px;
  border: 1px solid #eef0f3;
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.mmp-cat-item:hover { border-color: var(--el-color-primary); background: #fafbff; }
.mmp-cat-main { display: flex; flex-direction: column; min-width: 0; }
.mmp-cat-title { font-size: 14px; color: #1f2937; }
.mmp-cat-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  color: #9ca3af;
}
.mmp-cat-meta { display: flex; align-items: center; gap: 8px; color: #9ca3af; flex-shrink: 0; }
.mmp-cat-count { font-size: 12px; }

/* product rows */
.mmp-prod-list { margin-top: 4px; }
.mmp-prod-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #eef0f3;
  border-radius: 10px;
  margin-bottom: 8px;
}
.mmp-prod-item.is-disabled { opacity: 0.6; background: #f8f9fb; }
.mmp-prod-main { display: flex; flex-direction: column; min-width: 0; flex: 1; }
.mmp-prod-title {
  font-size: 14px;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.mmp-prod-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  color: #9ca3af;
}

.mmp-unit-chip {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 6px;
  background: #eef2ff;
  color: #4f46e5;
  font-size: 12px;
  font-weight: 500;
}
.mmp-unit-chip.is-muted { background: #f1f3f5; color: #6b7280; }

/* product table */
.mmp-cell-title { display: flex; align-items: center; gap: 8px; }
.mmp-hint-tag { flex-shrink: 0; }
.mmp-num { width: 100%; }
.mmp-num :deep(.el-input__inner) { text-align: right; }

.mmp-audit { display: flex; flex-direction: column; line-height: 1.3; }
.mmp-audit-name { font-size: 13px; color: #374151; }
.mmp-audit-date { font-size: 11px; color: #9ca3af; }

.mmp-row-actions { display: flex; gap: 8px; justify-content: center; }
.mmp-row-actions :deep(.el-button + .el-button) { margin-left: 0; }

:deep(.mmp-incomplete-row) { background: #fffdf5; }

/* board window banner */
.mmp-window-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid #fde68a;
  background: #fffbeb;
  color: #92400e;
  font-size: 13px;
  font-weight: 500;
}
.mmp-window-banner .el-icon { font-size: 18px; flex-shrink: 0; }

.mmp-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  font-size: 13px;
  color: #6b7280;
}

/* empty states */
.mmp-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 16px;
  color: #9ca3af;
  text-align: center;
}
.mmp-empty p { margin: 0; font-size: 14px; }
.mmp-empty--soft { padding: 36px 16px; }

@media (max-width: 1100px) {
  .mmp-workspace { grid-template-columns: 1fr; }
  .mmp-select { width: 100%; }
}
</style>
